import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import MetricCard from '../components/MetricCard'
import { traceService, type ObservabilityVerticalBreakdownRow } from '../services/traceService'

export type ObservabilitySummary = {
  windowMinutes?: number

  fallbackRate?: number
  escalationRate?: number
  loopRate?: number
  avgLatencyMs?: number
  latencyP95Ms?: number

  checkpointRestoreFailures?: number
  checkpointRestoreFailureRate?: number

  cragRejectRate?: number
  hitlTriggerRate?: number

  blockedFunnelRegressions?: number
  checkoutPromotions?: number
  checkoutPromotionBlocked?: number
  supportObjectionCount?: number
  acknowledgeRejectionCount?: number
  browseLoopCount?: number
  purchaseOscillationCount?: number
  verticalBreakdown?: ObservabilityVerticalBreakdownRow[]
  verticalGuardrailCounts?: Record<string, number>

  activeLongConversations?: number
  activeConversations?: number
}

function formatPercent(value: unknown): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return '-'
  return `${Math.round(n * 1000) / 10}%`
}

function formatNumber(value: unknown): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return '-'
  return String(Math.round(n))
}

function formatMs(value: unknown): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return '-'
  return `${Math.round(n)}ms`
}

function formatRateWithCount(rate: unknown, count: unknown): string {
  const rateLabel = formatPercent(rate)
  const n = Number(count)
  if (!Number.isFinite(n)) return rateLabel
  return `${rateLabel} (${Math.round(n)})`
}

export function ObservabilityOverviewView({
  tenantId,
  summary,
  loading,
  error,
  onRefresh,
}: {
  tenantId: string
  summary: ObservabilitySummary | null
  loading: boolean
  error: string
  onRefresh: () => void
}) {
  const windowLabel = summary?.windowMinutes ? `${summary.windowMinutes}m` : 'ventana'

  const cards = useMemo(() => {
    const verticalGuardrailTraceCount = (summary?.verticalBreakdown ?? []).reduce(
      (sum, row) => sum + (Number(row?.verticalGuardrailCount) || 0),
      0
    )

    return [
      {
        title: `Fallback rate (${windowLabel})`,
        value: formatPercent(summary?.fallbackRate),
        icon: 'FALL',
        type: (summary?.fallbackRate ?? 0) > 0.08 ? 'error' : (summary?.fallbackRate ?? 0) > 0.04 ? 'warning' : 'success',
      },
      {
        title: `Escalation rate (${windowLabel})`,
        value: formatPercent(summary?.escalationRate),
        icon: 'ESC',
        type: (summary?.escalationRate ?? 0) > 0.06 ? 'warning' : 'default',
      },
      {
        title: `Support objection (${windowLabel})`,
        value: formatNumber(summary?.supportObjectionCount),
        icon: 'OB',
        type: (summary?.supportObjectionCount ?? 0) > 0 ? 'warning' : 'success',
      },
      {
        title: `Acknowledge rejection (${windowLabel})`,
        value: formatNumber(summary?.acknowledgeRejectionCount),
        icon: 'RJ',
        type: (summary?.acknowledgeRejectionCount ?? 0) > 0 ? 'default' : 'success',
      },
      {
        title: `Loop rate (${windowLabel})`,
        value: formatPercent(summary?.loopRate),
        icon: 'LOOP',
        type: (summary?.loopRate ?? 0) > 0.05 ? 'warning' : 'default',
      },
      {
        title: `Browse loops (${windowLabel})`,
        value: formatNumber(summary?.browseLoopCount),
        icon: 'BRW',
        type: (summary?.browseLoopCount ?? 0) > 0 ? 'warning' : 'default',
      },
      {
        title: `Purchase oscillation (${windowLabel})`,
        value: formatNumber(summary?.purchaseOscillationCount),
        icon: 'OSC',
        type: (summary?.purchaseOscillationCount ?? 0) > 0 ? 'warning' : 'default',
      },
      {
        title: `Latency avg (${windowLabel})`,
        value: formatMs(summary?.avgLatencyMs),
        icon: 'LAT',
        type: (summary?.avgLatencyMs ?? 0) > 7000 ? 'warning' : 'default',
      },
      {
        title: `Vertical guardrails (${windowLabel})`,
        value: formatNumber(verticalGuardrailTraceCount),
        icon: 'VG',
        type: verticalGuardrailTraceCount > 0 ? 'warning' : 'success',
      },
      {
        title: `Checkpoint restore fails (${windowLabel})`,
        value: formatNumber(summary?.checkpointRestoreFailures),
        icon: 'CP',
        type: (summary?.checkpointRestoreFailures ?? 0) > 0 ? 'warning' : 'success',
      },
      {
        title: `Long convos (${windowLabel})`,
        value: formatNumber(summary?.activeLongConversations),
        icon: 'LONG',
        type: (summary?.activeLongConversations ?? 0) > 0 ? 'warning' : 'default',
      },
    ] as const
  }, [summary, windowLabel])

  const verticalRows = useMemo(
    () =>
      [...(summary?.verticalBreakdown ?? [])].sort(
        (a, b) =>
          (Number(b.frictionRate) || 0) - (Number(a.frictionRate) || 0) ||
          (Number(b.escalationRate) || 0) - (Number(a.escalationRate) || 0) ||
          (Number(b.traceCount) || 0) - (Number(a.traceCount) || 0) ||
          String(a.vertical).localeCompare(String(b.vertical))
      ),
    [summary?.verticalBreakdown]
  )

  const verticalGuardrailReasons = useMemo(
    () =>
      Object.entries(summary?.verticalGuardrailCounts ?? {})
        .sort(([, left], [, right]) => Number(right) - Number(left)),
    [summary?.verticalGuardrailCounts]
  )

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>Overview</div>
          <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.65)' }}>
            Señales operativas para detectar drift, loops, latencia y escalaciones.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ fontSize: 11, color: 'rgba(226,232,240,0.55)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>
            {tenantId ? `tenant=${tenantId}` : 'tenant no seleccionado'}
          </div>
          <button
            onClick={onRefresh}
            disabled={!tenantId || loading}
            style={{
              padding: '8px 10px',
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.04)',
              color: '#e2e8f0',
              fontSize: 12,
              fontWeight: 800,
              cursor: !tenantId || loading ? 'not-allowed' : 'pointer',
              opacity: !tenantId || loading ? 0.6 : 1,
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {!tenantId ? (
        <div style={{ padding: 14, borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.03)' }}>
          Selecciona un tenant para ver observabilidad.
        </div>
      ) : loading ? (
        <div style={{ padding: 14, borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.03)' }}>
          Cargando observabilidad...
        </div>
      ) : error ? (
        <div style={{ padding: 14, borderRadius: 12, border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.08)', color: '#fecaca' }}>
          {error}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginTop: 12 }}>
            {cards.map((card) => (
              <MetricCard
                key={card.title}
                title={card.title}
                value={card.value}
                icon={card.icon}
                type={card.type as any}
              />
            ))}
          </div>

          <div style={{ marginTop: 14, border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, background: 'rgba(255,255,255,0.02)', padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800 }}>Segmentacion por vertical</div>
                <div style={{ fontSize: 11, color: 'rgba(226,232,240,0.65)' }}>
                  Friccion = fallback, loop o guardrail. Ordenado por friccion para detectar los verticales mas sensibles.
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'rgba(226,232,240,0.55)' }}>
                {verticalRows.length ? `${verticalRows.length} verticales` : 'Sin datos por vertical'}
              </div>
            </div>

            {verticalRows.length > 0 ? (
              <div style={{ marginTop: 12, overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'rgba(226,232,240,0.75)' }}>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.10)' }}>Vertical</th>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.10)' }}>Traces</th>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.10)' }}>Friccion</th>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.10)' }}>Escalada</th>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.10)' }}>Guardrails verticales</th>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.10)' }}>Latencia avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {verticalRows.map((row) => (
                      <tr key={row.vertical} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <td style={{ padding: '8px 10px', fontWeight: 800, color: '#e2e8f0' }}>{row.vertical}</td>
                        <td style={{ padding: '8px 10px', color: '#cbd5e1' }}>{formatNumber(row.traceCount)}</td>
                        <td style={{ padding: '8px 10px', color: '#cbd5e1' }}>
                          {formatRateWithCount(row.frictionRate, row.frictionCount)}
                        </td>
                        <td style={{ padding: '8px 10px', color: '#cbd5e1' }}>
                          {formatRateWithCount(row.escalationRate, row.escalationCount)}
                        </td>
                        <td style={{ padding: '8px 10px', color: '#cbd5e1' }}>
                          {formatRateWithCount(row.verticalGuardrailRate, row.verticalGuardrailCount)}
                        </td>
                        <td style={{ padding: '8px 10px', color: '#cbd5e1' }}>{formatMs(row.avgLatencyMs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 12, border: '1px dashed rgba(255,255,255,0.12)', color: 'rgba(226,232,240,0.6)' }}>
                Aun no hay trazas suficientes para segmentar por vertical.
              </div>
            )}

            {verticalGuardrailReasons.length > 0 ? (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, marginBottom: 6 }}>vertical_guardrail:* observados</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {verticalGuardrailReasons.map(([signal, count]) => (
                    <span
                      key={signal}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 10px',
                        borderRadius: 999,
                        border: '1px solid rgba(255,255,255,0.10)',
                        background: 'rgba(255,255,255,0.04)',
                        color: '#cbd5e1',
                        fontSize: 11,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span>{signal}</span>
                      <span style={{ color: 'rgba(226,232,240,0.55)' }}>{formatNumber(count)}</span>
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginTop: 14 }}>
            <div style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, background: 'rgba(255,255,255,0.02)', padding: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Acciones</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Link className="obs-link" to="/admin/debug/observability/alerts">Ver alertas</Link>
                <Link className="obs-link" to="/admin/debug/observability/funnel">Ver salud del funnel</Link>
                <Link className="obs-link" to="/admin/debug/observability/conversations">Ver conversaciones largas</Link>
              </div>
              <div style={{ fontSize: 11, color: 'rgba(226,232,240,0.55)', marginTop: 10 }}>
                Tip: usa el Trace Inspector para un caso puntual; usa este overview para salud operativa.
              </div>
            </div>

            <div style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, background: 'rgba(255,255,255,0.02)', padding: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Lectura rapida</div>
              <div style={{ fontSize: 12, lineHeight: 1.55, color: 'rgba(226,232,240,0.85)' }}>
                <div>
                  Si sube <b>Fallback</b> o <b>Checkpoint restore fails</b>, revisa `GRAPH_CHECKPOINT_LOAD_FAILED` y `SAFE_FALLBACK_TRIGGERED` en trazas recientes.
                </div>
                <div style={{ marginTop: 6 }}>
                  Si sube <b>Loop</b>, revisa `LOOP_RISK` y `GRAPH_LOOP_RESCUE_FALLBACK`, y confirma el stageHistory.
                </div>
                <div style={{ marginTop: 6 }}>
                  Si sube <b>Escalation</b>, valida que sea por `hitl` o por `loop_risk`, no por drift de funnel.
                </div>
                <div style={{ marginTop: 6 }}>
                  Si suben <b>Support objection</b> o <b>Acknowledge rejection</b>, revisa `SUPERVISOR_OVERRIDE` y la severidad del `OBJECTION` node.
                </div>
                <div style={{ marginTop: 6 }}>
                  Si suben los <b>Vertical guardrails</b>, revisa `GUARDRAIL_OVERRIDE` y compara la tabla por vertical para ver donde se esta frenando el funnel.
                </div>
              </div>
              <details style={{ marginTop: 10 }}>
                <summary style={{ fontSize: 11, color: 'rgba(226,232,240,0.65)', cursor: 'pointer' }}>Ver contrato (campos)</summary>
                <pre style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap', fontSize: 11, color: '#a7f3d0', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.18)', borderRadius: 12, padding: 12 }}>
{JSON.stringify(summary, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default function DebugObservabilityOverview({
  tenantId,
  minutes = 1440,
}: {
  tenantId: string
  minutes?: number
}) {
  const [summary, setSummary] = useState<ObservabilitySummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const refresh = async () => {
    if (!tenantId) return
    setLoading(true)
    setError('')
    try {
      const res = await traceService.getObservabilitySummary({ tenantId, minutes })
      setSummary(res.data ?? null)
    } catch {
      setSummary(null)
      setError('No se pudo cargar el summary de observabilidad')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [tenantId, minutes])

  return (
    <ObservabilityOverviewView
      tenantId={tenantId}
      summary={summary}
      loading={loading}
      error={error}
      onRefresh={refresh}
    />
  )
}
